import {
  guid,
  ID
} from '@datorama/akita';
import {
  CardTypeEnum,
  ChartFilterTypeEnum,
  ChartType,
  ChartTypeEnum,
  CustomControlsNameEnum,
  DateType,
  IChartFilter,
  IChartFilterPattern,
  SingleContractAnalyzerTypeEnum,
  IOptionInfoStrikes,
  RealTimeEnum,
  Strike,
  TimeRangeEnum,
  TotalOIEnum,
  OptionTypeEnum
} from '../interfaces';
import { ICard } from './card';
import {
  IChartSeries,
  SeriesTypeEnum
} from 'app/charts-dashboard/state/series';
import {
  Instrument,
  StockTypeEnum
} from '@iv/model/instrument';

export const initialSeries = (): IChartSeries => {
  return {
    id: guid(),
    stock: new Instrument(9327, 1, 'SPX', 'CBOE', 'S&P 500 Index (SPX)', 'USA', StockTypeEnum.INDEX, '1988-01-04T00:00:00', '2079-06-06T00:00:00', false),
    selectedFilters: {
      timeRange: {
        name: TimeRangeEnum.Y1,
        description: '1 year - daily frequency'
      },
      realtime: {
        name: RealTimeEnum.ON,
        description: RealTimeEnum.ON
      },
      vModeToggler: false
    },
    settings: {
      color: '#ed4545'
    },
    type: SeriesTypeEnum.MAIN
  };
};

export const initialSeriesSPYSpotPrice = (): IChartSeries => {
  return {
    id: guid(),
    stock: new Instrument(627, 1, 'SPY', 'NYSEArca', 'SPDR S&P 500 ETF Trust', 'USA', StockTypeEnum.INDEX, '2010-01-28T00:00:00', '2079-06-06T00:00:00', false),
    selectedFilters: {
      timeRange: {
        name: TimeRangeEnum.Y1,
        description: '1 year - daily frequency'
      },
      realtime: {
        name: RealTimeEnum.ON,
        description: RealTimeEnum.ON
      },
      vModeToggler: false
    },
    settings: {
      color: '#ed4545'
    },
    type: SeriesTypeEnum.MAIN
  };
};
export const initialSeriesSPYImpliedVolatility = (): IChartSeries => {
  return {
    id: guid(),
    stock: new Instrument(627, 1, 'SPY', 'NYSEArca', 'SPDR S&P 500 ETF Trust', 'USA', StockTypeEnum.INDEX, '2010-01-28T00:00:00', '2079-06-06T00:00:00', false),
    selectedFilters: {
      moneyness: {
        name: 'M100',
        description: "ATM"
      },
      ivxTerm: {
        name: 'T360',
        description: "360"
      },
      timeRange: {
        name: TimeRangeEnum.Y1,
        description: '1 year - daily frequency'
      },
      realtime: {
        name: RealTimeEnum.ON,
        description: RealTimeEnum.ON
      },
      vModeToggler: false
    },
    settings: {
      color: '#45ed55'
    },
    type: SeriesTypeEnum.MAIN
  };
};
export const initialSeriesSPYHistoricalVolatilityHV180 = (): IChartSeries => {
  return {
    id: guid(),
    stock: new Instrument(627, 1, 'SPY', 'NYSEArca', 'SPDR S&P 500 ETF Trust', 'USA', StockTypeEnum.INDEX, '2010-01-28T00:00:00', '2079-06-06T00:00:00', false),
    selectedFilters: {
      eodTimeRange: {
        name: TimeRangeEnum.Y1,
        description: '1 year - daily frequency'
      },
      hvTerm: {
        name: 'T180',
        description: "HV 180"
      },
      vModeToggler: false
    },
    settings: {
      color: '#690606'
    },
    type: SeriesTypeEnum.MAIN
  };
};
export const initialSeriesSPYHistoricalVolatilityHV10 = (): IChartSeries => {
  return {
    id: guid(),
    stock: new Instrument(627, 1, 'SPY', 'NYSEArca', 'SPDR S&P 500 ETF Trust', 'USA', StockTypeEnum.INDEX, '2010-01-28T00:00:00', '2079-06-06T00:00:00', false),
    selectedFilters: {
      eodTimeRange: {
        name: TimeRangeEnum.Y1,
        description: '1 year - daily frequency'
      },
      hvTerm: {
        name: 'T10',
        description: "HV 10"
      },
      vModeToggler: false
    },
    settings: {
      color: '#652e6d'
    },
    type: SeriesTypeEnum.MAIN
  };
};
export const initialSeriesSPYTotalOIVolumeCall = (): IChartSeries => {
  return {
    id: guid(),
    stock: new Instrument(627, 1, 'SPY', 'NYSEArca', 'SPDR S&P 500 ETF Trust', 'USA', StockTypeEnum.INDEX, '2010-01-28T00:00:00', '2079-06-06T00:00:00', false),
    selectedFilters: {
      eodTimeRange: {
        name: TimeRangeEnum.Y1,
        description: '1 year - daily frequency'
      },
      optionType: {
        name: OptionTypeEnum.CALL,
        description: 'Call'
      },
      totalOiChartType: {
        name: TotalOIEnum.VOLUME,
        description: 'Volume'
      },
      vModeToggler: false
    },
    settings: {
      color: '#c52626'
    },
    type: SeriesTypeEnum.MAIN
  };
};
export const initialSeriesSPYTotalOIVolumePut = (): IChartSeries => {
  return {
    id: guid(),
    stock: new Instrument(627, 1, 'SPY', 'NYSEArca', 'SPDR S&P 500 ETF Trust', 'USA', StockTypeEnum.INDEX, '2010-01-28T00:00:00', '2079-06-06T00:00:00', false),
    selectedFilters: {
      eodTimeRange: {
        name: TimeRangeEnum.Y1,
        description: '1 year - daily frequency'
      },
      optionType: {
        name: OptionTypeEnum.PUT,
        description: 'Put'
      },
      totalOiChartType: {
        name: TotalOIEnum.VOLUME,
        description: 'Volume'
      },
      vModeToggler: false
    },
    settings: {
      color: '#130669'
    },
    type: SeriesTypeEnum.MAIN
  };
};

export const initialCard = (chartType: ChartType = ChartTypeEnum.SPOT_CHART): ICard<ID> => {
  return {
    id: guid(),
    type: CardTypeEnum.FILLED,
    chartSeries: [],
    chartId: chartType,
    filters: [],
    fullscreen: false
  };
};

export const addNewCard = (): ICard<ID> => {
  return {
    id: guid(),
    type: CardTypeEnum.ADD_NEW_CARD,
    fullscreen: false
  };
};

export const voidCard = (): ICard<ID> => {
  return {
    id: guid(),
    type: CardTypeEnum.VOID,
    fullscreen: false
  };
};

export function addSingleContractAnalyzerExpirations(expDates: string[]): IChartFilter[] {
  return [
    {
      parameterId: CustomControlsNameEnum.EXPIRATION_DATE,
      name: 'Expiration date',
      type: ChartFilterTypeEnum.CUSTOM,
      pattern: expDates.map((exp: string) => <IChartFilterPattern<DateType>>{description: exp, name: exp}),
      description: 'Option expiration date'
    }
  ];
}

export function addSingleContractAnalyzerStrikes(optInfoStrikes: IOptionInfoStrikes): IChartFilter[] {
  return [
    {
      parameterId: CustomControlsNameEnum.STRIKE,
      name: 'Strike',
      type: ChartFilterTypeEnum.CUSTOM,
      pattern: optInfoStrikes.strikes.map((strike: number) => <IChartFilterPattern<Strike>>{description: strike.toString(), name: strike}),
      description: 'Option strike'
    },
    {
      parameterId: CustomControlsNameEnum.SINGLE_CONTRACT_ANALYZER_TYPE,
      name: 'Chart type',
      type: ChartFilterTypeEnum.CUSTOM,
      pattern: [
        {
          name: SingleContractAnalyzerTypeEnum.BID,
          description: 'Bid'
        },

        {
          name: SingleContractAnalyzerTypeEnum.ASK,
          description: 'Ask'
        },
        {
          name: SingleContractAnalyzerTypeEnum.LAST_PRICE,
          description: 'Last price'
        },
        {
          name: SingleContractAnalyzerTypeEnum.IV,
          description: 'IV'
        }
      ],
      description: 'Intraday chart type'
    }
  ];
}

export const totalOiChartType: IChartFilter[] = [
  {
    parameterId: CustomControlsNameEnum.TOTAL_OI_CHART_TYPE,
    name: 'Type',
    pattern: [
      {
        name: TotalOIEnum.OPEN_INTEREST,
        description: 'Open Interest'
      },
      {
        name: TotalOIEnum.VOLUME,
        description: 'Volume'
      }
    ],
    description: 'Total open interest type chart',
    type: ChartFilterTypeEnum.ENUM
  }
];
